import Panel from '../components/panel.tsx'

import { useStore } from "../state/state.ts";
import ModalBase1 from "./modal_base_1.tsx";

import ModalConfirmButton from '../components/modal_close_button.tsx';
//import './modal_building_geometry_data.css';
import { azimuth_deg_to_roof_orientation } from '../functions.ts';

type Row = [label: string, value: unknown, unit?: string];

/** Formatiert einen beliebigen Dossier-Wert für die tabellarische Anzeige. */
function format_value(value: unknown, unit?: string): string {
    if (value === null || value === undefined || value === "") {
        return "–";
    }
    if (typeof value === "boolean") {
        return value ? "ja" : "nein";
    }
    if (typeof value === "number") {
        const formatted = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value);
        return unit ? `${formatted} ${unit}` : formatted;
    }
    return unit ? `${String(value)} ${unit}` : String(value);
}

/** Zwei-Spalten-Tabelle (Bezeichnung / Wert) für einen Dossier-Abschnitt. */
function DossierTable(props: { title: string, rows: Row[] }) {
    return (<section className="dossier-section">
        <h2>{props.title}</h2>
        <table className="dossier-table" cellSpacing="0" cellPadding="0" border={0}>
            <tbody>
                {props.rows.map(([label, value, unit]) => (
                    <tr key={label}>
                        <td>{label}</td>
                        <td>{format_value(value, unit)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </section>);
}

/** Verteilungen (Record<string, number>) aus dem Zensus als Tabelle. */
function DistributionTable(props: { title: string, distribution?: Record<string, number> }) {
    const entries = Object.entries(props.distribution ?? {});
    if (entries.length === 0) {
        return null;
    }
    return (<section className="dossier-section">
        <h2>{props.title}</h2>
        <table className="dossier-table" cellSpacing="0" cellPadding="0" border={0}>
            <tbody>
                {entries.map(([key, value]) => (
                    <tr key={key}>
                        <td>{key}</td>
                        <td>{format_value(value)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </section>);
}

export default function ModalBuildingGeometryData() {
    const pop_modal = useStore((state) => state.pop_modal);
    const api_recalculate = useStore((state) => state.api_recalculate);
    const dossier = useStore((state) => state.building_dossier);
    const municipality = dossier?.municipality;
    const climate = dossier?.climate;
    const zones = dossier?.zones;
    const census = dossier?.census;
    const heat_planning = dossier?.heat_planning;
    const parcel_buildings = useStore((state) => state.parcel_buildings);

    return (<Panel>
        <h1 id="modal-title">Gebäudedaten</h1>
        {parcel_buildings && parcel_buildings.length > 0 && <h2>Hauptgebäude</h2>}
        {dossier === undefined
            ? <p id="modal-description">Für dieses Gebäude liegen keine Zusatzinformationen vor.</p>
            : <>
                <DossierTable title="Gebäude" rows={[
                    ["Gebäude-ID", dossier.building_id],
                    ["Höhe über NN", dossier.elevation_m, "m"],
                    ["Flurstück", dossier.parcel?.label],
                    ["Flurstück gültig ab", dossier.parcel?.valid_from],
                ]} />

                {municipality && <DossierTable title="Gemeinde" rows={[
                    ["Name", municipality.name],
                    ["Art", municipality.type],
                    ["AGS", municipality.ags],
                    ["Kreis", municipality.kreis],
                    ["Verbund", municipality.verbund],
                    ["Bundesland", municipality.land],
                    ["Einwohner", municipality.population],
                    ["Fläche", municipality.area_km2, "km²"],
                ]} />}

                {climate && <DossierTable title="Klima" rows={[
                    ["Klimaregion", climate.region_name],
                    ["Regionscode", climate.region_code],
                    ["Heiztage", climate.heating_days],
                    ["Norm-Außentemperatur", climate.theta_e_base, "°C"],
                    ["Klimafaktor", climate.climate_factor],
                    ["Mittlere Temperatur", climate.temp_mean_c, "°C"],
                    ["Globalstrahlung", climate.radiation_kwh_m2a, "kWh/m²a"],
                ]} />}

                {zones && <DossierTable title="Zonen" rows={[
                    ["Schneelastzone", zones.snow_zone],
                    ["Hinweis Schneelastzone", zones.snow_zone_note],
                    ["Schneelast", zones.snow_load_kn_m2, "kN/m²"],
                    ["Windzone", zones.wind_zone],
                    ["Mietenstufe", zones.mietenstufe],
                ]} />}

                {heat_planning && <>
                    <DossierTable title="Wärmeplanung" rows={[
                        ["Planungsstand", heat_planning.plan_status],
                        ["Planungsjahr", heat_planning.plan_year],
                        ["Fernwärme", heat_planning.fernwaerme],
                        ["Betreiber", heat_planning.fernwaerme_operator],
                        ["Ausbau", heat_planning.fernwaerme_expansion],
                        ["Verlässlichkeit", heat_planning.confidence],
                        ["Gebiet", heat_planning.area?.name],
                        ["Gebietstyp", heat_planning.area?.kind],
                        ["Zieljahr", heat_planning.area?.target_year],
                    ]} />

                    {heat_planning.prices && heat_planning.prices.length > 0 &&
                        <section className="dossier-section">
                            <h2>Fernwärmepreise</h2>
                            <table className="dossier-table" cellSpacing="0" cellPadding="0" border={0}>
                                <thead>
                                    <tr>
                                        <th>Netz</th>
                                        <th>gültig ab</th>
                                        <th>Arbeitspreis (ct/kWh, netto)</th>
                                        <th>Leistungspreis (€/kW a, netto)</th>
                                        <th>Grundpreis (€/a, netto)</th>
                                        <th>Messpreis (€/a, netto)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {heat_planning.prices.map((price) => (
                                        <tr key={`${price.network}-${price.valid_from}`}>
                                            <td>{format_value(price.network)}</td>
                                            <td>{format_value(price.valid_from)}</td>
                                            <td>{format_value(price.arbeitspreis_ct_kwh_netto)}</td>
                                            <td>{format_value(price.leistungspreis_eur_kw_a_netto)}</td>
                                            <td>{format_value(price.grundpreis_eur_a_netto)}</td>
                                            <td>{format_value(price.messpreis_eur_a_netto)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>}
                </>}

                {census && <DossierTable title="Zensus" rows={[
                    ["Gebäude insgesamt", census.buildings_total],
                ]} />}
                <DistributionTable title="Zensus – Baujahr" distribution={census?.baujahr} />
                <DistributionTable title="Zensus – Heizungsart" distribution={census?.heizungsart} />
                <DistributionTable title="Zensus – Energieträger" distribution={census?.energietraeger} />

                
            </>}

        {parcel_buildings && parcel_buildings.length > 0 && <>
            <h2>Nebengebäude</h2>
            {parcel_buildings.map((building) => (
                <div key={building.building_id}>
                    <DossierTable title={building.function_label || building.building_id} rows={[
                        ["Gebäude-ID", building.building_id],
                        ["Nutzung", building.function_label],
                        ["Nutzungscode", building.function_code],
                        ["Grundfläche", building.footprint_m2, "m²"],
                        ["Dachfläche", building.roof_area_m2, "m²"],
                        ["Dachform", building.roof_type_label],
                        ["Dachform-Code", building.roof_type_code],
                        ["Traufhöhe", building.eaves_height_m, "m"],
                        ["Firsthöhe", building.ridge_height_m, "m"],
                        ["Geometriequelle", building.geometry_source],
                        ["Stand", building.source_date],
                    ]} />

                    {building.roof_segments && building.roof_segments.length > 0 &&
                        <section className="dossier-section">
                            <h2>Dachflächen</h2>
                            <table className="dossier-table" cellSpacing="0" cellPadding="0" border={0}>
                                <thead>
                                    <tr>
                                        <th>Fläche (m²)</th>
                                        <th>Neigung (°)</th>
                                        <th>Azimut (°)</th>
                                        <th>Ausrichtung</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {building.roof_segments.map((segment, index) => (
                                        <tr key={index}>
                                            <td>{format_value(segment.area_m2)}</td>
                                            <td>{format_value(segment.tilt_deg)}</td>
                                            <td>{format_value(segment.azimuth_deg)}</td>
                                            <td>{format_value(azimuth_deg_to_roof_orientation(segment.azimuth_deg))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>}
                </div>
            ))}
        </>}

{dossier.attribution && dossier.attribution.length > 0 &&
                    <p className="dossier-attribution">Quellen: {dossier.attribution.map((a) => <span className='attribution-source'>{a}</span>)}</p>}

        <ModalConfirmButton label={'schließen'} onClick={() => {
           api_recalculate();
           pop_modal();
        }} />
    </Panel>)
}
